import {useState, useMemo} from 'react'
import {useAppSelector, useCreateCsv} from 'src/hooks'
import {selectAllSortedSessions} from 'src/app/allSessions'
import {selectGroupIds} from 'src/app/hostSettings'
import {normalizeParticipantData} from 'src/utils/normalizeCSV'
import {mergeParticipantsData} from 'src/utils/helpers'
import SessionCard from 'src/components/session/SessionCard'
import FilterDropdown from 'src/components/Dropdown/FilterDropdown'
import BackButton from '../Navigation/BackButton'
import CSVButton from '../CSVButton'
import {Session, Participants} from 'src/app/types'
import {selectPreviousPage} from 'src/app/page'

const AllSessions = () => {
    const sortedSessions: Session[] = useAppSelector(selectAllSortedSessions)
    const numSessions = String(sortedSessions.length)
    const previousPage = useAppSelector(selectPreviousPage)
    const allSessionsText = `All Sessions (${numSessions})`
    const namesOfGroups = useAppSelector(selectGroupIds)
    const [isTagsFilter, setIsTagsFilter] = useState<boolean>(true)
    const namesOfGroupsList = useMemo(() => {
        const allSessionsGroupIds: string[] = sortedSessions.map(
            ({groupId = ''}) => groupId
        )
        const allSessionsGroupNames = allSessionsGroupIds
            .map((groupId) => namesOfGroups[groupId])
            .filter(Boolean)
        return allSessionsGroupNames
    }, [sortedSessions, namesOfGroups])
    const namesOfGroupsSet = useMemo(
        () => new Set(namesOfGroupsList),
        [namesOfGroupsList]
    )
    const namesOfGroupsListDedup = useMemo(
        () => Array.from(namesOfGroupsSet),
        [namesOfGroupsSet]
    )

    const [selectedGroups, setSelectedGroups] = useState<string[]>(
        []
    )

    const handleSelectOptions = (selectedGroups: string[] = []) => {
        setIsTagsFilter(false)
        setSelectedGroups(selectedGroups)
    }
    const selectedGroupNamesSet = useMemo(
        () => new Set(selectedGroups),
        [selectedGroups]
    )
    const displayedSessions = useMemo(
        () => {
            if (selectedGroups.length >= 1)
                return sortedSessions.filter(({groupId = ''}) =>
                    selectedGroupNamesSet.has(namesOfGroups[groupId])
                )
            return sortedSessions
        },
        [namesOfGroups, selectedGroupNamesSet, sortedSessions, selectedGroups.length]
    )

    // CSV logic

    const displayedParticipants: Participants[] = displayedSessions.map(
        ({participants}) => participants
    )
    const mergedParticipants = mergeParticipantsData(
        structuredClone(displayedParticipants)
    )
    const {normalizedParticipants, allBadges} = normalizeParticipantData(
        Object.values(mergedParticipants)
    )

    const {isLoading, onExportToCSV} = useCreateCsv({
        allBadges,
        normalizedParticipants,
        name: 'All Sessions',
    })

    return (
        <div className="w-full bg-white">
            <BackButton page={previousPage}/>
            <div className="flex justify-between mt-3 mb-4">
                <h2 className="text-base font-bold mt-1 mb-3 text-darkBlack sm:font-[500] sm:text-sm">
                    {allSessionsText}
                </h2>
                <div className="flex justify-end w-24">
                    <CSVButton
                        isLoading={isLoading}
                        type="secondary"
                        onExportToCSV={onExportToCSV}
                        disabled={normalizedParticipants.length <= 0}
                        text={'Export All'}
                    />
                </div>
            </div>
            <FilterDropdown
                name="All Tags"
                items={namesOfGroupsListDedup}
                setSelectedOptions={handleSelectOptions}
                selectedValues={selectedGroups}
                selectedText={{
                    singularText: 'Filter by Tags',
                    pluralText: 'Filter by Tags',
                }}
                isTags={isTagsFilter}
            />
            <div className="gap-3 mt-6 grid md:grid-cols-cards">
                {displayedSessions.map((session) => (
                    <SessionCard key={session.id} {...(session as Required<Session>)} />
                ))}

            </div>
        </div>
    )
}

export default AllSessions
